import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { RcsPath } from 'pages/settings/pages/rcs/route/rcsPath'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { type RcsWizardStore } from './rcsWizardStore'

const titleMap: Record<RcsWizardSteps, string> = {
  [RcsWizardSteps.senderDetails]: 'Sender details',
  [RcsWizardSteps.countries]: 'Countries',
  [RcsWizardSteps.businessInfo]: 'Business info',
  [RcsWizardSteps.contactInfo]: 'Contact info',
  [RcsWizardSteps.useCase]: 'Use case',
  [RcsWizardSteps.optInMessageInfo]: 'Opt-in & message info',
  [RcsWizardSteps.review]: 'Review and confirm',
}

const apiStepMap: Record<RcsWizardSteps, number> = {
  [RcsWizardSteps.senderDetails]: 1,
  [RcsWizardSteps.countries]: 2,
  [RcsWizardSteps.businessInfo]: 3,
  [RcsWizardSteps.contactInfo]: 4,
  [RcsWizardSteps.useCase]: 5,
  [RcsWizardSteps.optInMessageInfo]: 6,
  [RcsWizardSteps.review]: 7,
}

export class RcsWizardNavigationStore {
  constructor(private _wizardStore: RcsWizardStore) {
    makeAutoObservable(this)
  }

  get rootPath() {
    return RcsPath.create
  }

  get steps(): RcsWizardSteps[] {
    return [
      RcsWizardSteps.senderDetails,
      RcsWizardSteps.countries,
      ...(this._wizardStore.isUsFlow ? [RcsWizardSteps.businessInfo] : []),
      RcsWizardSteps.contactInfo,
      RcsWizardSteps.useCase,
      RcsWizardSteps.optInMessageInfo,
      RcsWizardSteps.review,
    ]
  }

  get allStepsLength() {
    return this.steps.length
  }

  getStepFromPath = (segment: string): RcsWizardSteps =>
    this.steps.find((step) => step === segment) ?? RcsWizardSteps.senderDetails

  getStepNumber = (step: RcsWizardSteps) => {
    const index = this.steps.indexOf(step)
    return index === -1 ? 1 : index + 1
  }

  getApiStep = (step: RcsWizardSteps) => apiStepMap[step]

  getStepFromApiStep = (apiStep: number): RcsWizardSteps =>
    this.steps.find((step) => apiStepMap[step] === apiStep) ?? RcsWizardSteps.senderDetails

  getStepForContinue = (lastApiStep: number | null): RcsWizardSteps => {
    if (!lastApiStep) return RcsWizardSteps.senderDetails

    const lastSavedStep = this.getStepFromApiStep(lastApiStep)
    const nextIndex = this.steps.indexOf(lastSavedStep) + 1

    return this.steps[nextIndex] ?? lastSavedStep
  }

  getSettingsStep = (step: RcsWizardSteps) => this.getStepNumber(step)

  getProgressInfoTitle = (step: RcsWizardSteps) => titleMap[step]

  getStepUrl = (step: RcsWizardSteps) => `${this.rootPath}/${step}`

  getAdjacentUrl = (current: RcsWizardSteps, direction: -1 | 1) => {
    const index = this.steps.indexOf(current)
    const adjacent = this.steps[index + direction]

    return this.getStepUrl(adjacent || current)
  }

  goToStep = (step: RcsWizardSteps) => {
    uiStore.changeRoute({ path: this.getStepUrl(step) })
  }

  goToNextStep = (current: RcsWizardSteps) => {
    uiStore.changeRoute({ path: this.getAdjacentUrl(current, 1) })
  }

  decreaseStep = (lastPathSegment: string) => {
    uiStore.changeRoute({ path: this.getAdjacentUrl(this.getStepFromPath(lastPathSegment), -1) })
  }
}
